'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'octor-theme';
const MEDIA_QUERY = '(prefers-color-scheme: dark)';

const THEME_COLOR: Record<ResolvedTheme, string> = {
  light: '#f3f3f5',
  dark: '#131314',
};

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  return window.matchMedia(MEDIA_QUERY).matches ? 'dark' : 'light';
}

function readStoredTheme(): Theme {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    if (value === 'light' || value === 'dark' || value === 'system') {
      return value;
    }
  } catch {
    // localStorage unavailable (privacy mode, etc.) — fall through to 'system'.
  }
  return 'system';
}

function applyToDom(resolved: ResolvedTheme) {
  document.documentElement.setAttribute('data-theme', resolved);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', THEME_COLOR[resolved]);
  }
}

type ThemeContextValue = {
  theme: Theme;
  resolvedTheme: ResolvedTheme | null;
  setTheme: (theme: Theme) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  // `null` until the first client-side sync: the initial render must match the
  // server output, and the DOM attribute is owned by the pre-hydration inline
  // script until then.
  const [theme, setThemeState] = useState<Theme | null>(null);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme | null>(null);

  // First sync after hydration: adopt whatever the inline script already put
  // on <html> instead of overwriting it, and read the stored preference.
  useEffect(() => {
    const stored = readStoredTheme();
    const system = getSystemTheme();
    const domTheme = document.documentElement.getAttribute('data-theme');
    const resolved: ResolvedTheme =
      domTheme === 'dark' || domTheme === 'light'
        ? domTheme
        : stored === 'system'
          ? system
          : stored;

    setThemeState(stored);
    setResolvedTheme(resolved);

    // Only touch the DOM if the inline script did not run / left no attribute.
    if (domTheme !== 'dark' && domTheme !== 'light') {
      applyToDom(resolved);
    } else {
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) {
        meta.setAttribute('content', THEME_COLOR[resolved]);
      }
    }
  }, []);

  // Re-apply whenever the resolved theme changes (including via setTheme).
  useEffect(() => {
    if (!resolvedTheme) return;
    applyToDom(resolvedTheme);
  }, [resolvedTheme]);

  // Follow OS preference while the stored preference is 'system'.
  useEffect(() => {
    if (theme !== 'system') return;
    const media = window.matchMedia(MEDIA_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      setResolvedTheme(event.matches ? 'dark' : 'light');
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Ignore storage failures; in-memory state still applies the theme.
    }
    setThemeState(next);
    setResolvedTheme(next === 'system' ? getSystemTheme() : next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme: theme ?? 'system', resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
